import React, { useContext, useEffect, useState } from 'react';
import { Card, CardContent, Typography, Button, Grid, CardActionArea, TextField, InputBase, withStyles, alpha, FormControl, InputLabel, CardActions, CircularProgress, FormHelperText } from '@material-ui/core';
import PartnerStakeStyle from './PartnerStakeStyle';
import { Colors } from '../../../helpers/theme';
import { getTokenBalance } from '../../../utils/web3/callHelpers';
import AlertContext from '../../../store/Alert/AlertContext';
import WalletContext from '../../../store/Wallet/WalletContext';
import formatDuration from '../../../helpers/MomentDuration';

const AmountInput = withStyles((theme) => ({
  root: {
    'label + &': {
      marginTop: theme.spacing(2),
    },
  },
  input: {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: theme.palette.common.white,
    border: '1px solid #ced4da',
    fontSize: 16,
    padding: '10px 12px',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    // Use the system font instead of the default Roboto font.
    '&:focus': {
      borderColor: theme.palette.primary.main,
    },
    '&:Mui-disabled': {
      backgroundColor: Colors.greySlate,
    }
  },
}))(InputBase);

const PartnerStakeApproveCard: React.FC<any> = ({ pool, stakeConfig, tokenName, toggleView, hasStaked, toggleHasStaked, address, setTotalStaked, setTotalStakedUser, toggleSuccessModal, totalStaked, maxStakeDuration }) => {
  
  const classes = PartnerStakeStyle();
  const { setAlert } = useContext<any>(AlertContext);
  const { approveTokenSpent, stakeToken } = useContext<any>(WalletContext);

  // Get token balance
  // Disable button - amount null or zero, amount > balance, amount + totalStaked > maxCap, or approval < amount and not error ( ? )

  const [amount, setAmount] = useState<string | null>();
  const [isAmountValid, toggleAmountValid] = useState<boolean>(false);
  const [tokenBalance, setTokenBalance] = useState<number>(0);
  const [approved, toggleApproved] = useState<boolean>(false);
  const [loading, toggleLoading] = useState<boolean>(false);
  const [error, setError] = React.useState('');

  useEffect(() => {
    if (address) {
      getTokenBalance(stakeConfig.networkId, address, stakeConfig.tokenAddress)
      .then((balance: number) => {
        setTokenBalance(balance);
      })
      .catch((error: any) => {
        setAlert('Unable to fetch token balance', 'error');
      })
    } else {
      setTokenBalance(0);
    }
  }, [address])

  useEffect(() => {
    if (amount == '' || !amount) {
      toggleAmountValid(false);
      return;
    };
    let amt = Number(amount);
    if (amt && amt > 0) {
      // TODO: validate amount
      if (amt < tokenBalance) {
        if (amt + totalStaked < stakeConfig.maxCap) {
          toggleAmountValid(true);
          setError('');
        } else {
          toggleAmountValid(false);
          setError('Current amount exceeds max cap');
        }
      } else {
        toggleAmountValid(false);
        setError('Amount cannot be more than balance')
      }
    } else {
      toggleAmountValid(false);
      setError('Amount should be more than 0')
    }
  }, [amount]);

  const handleStakeAndApprove = () => {
    toggleLoading(true)
    if (!approved) {
      approveTokenSpent(stakeConfig.networkId, amount, stakeConfig.tokenAddress, stakeConfig.poolStakeAddress)
      .then((transactionHash: any) => {
        setAlert('Token spend successfully approved', 'success');
        toggleApproved(true);
        toggleLoading(false);
      })
      .catch((error: any) => {
        setAlert('Failed to approve token spend', 'error');
        toggleLoading(false);
      })
    } else {
      stakeToken(amount, stakeConfig.poolStakeAddress, stakeConfig.networkId, stakeConfig.tokenAddress)
      .then(() => {
        setTotalStaked(amount);
        setTotalStakedUser(amount);
        toggleSuccessModal();
        toggleHasStaked(true);
        toggleView();
        toggleLoading(false);
      })
      .catch(() => {
        setAlert('Failed to stake tokens, please try again later', 'error');
        toggleLoading(false);
      })
    }
  }

  return (
    <Card className={classes.cardRoot}>
      <CardContent>
        <Typography variant="subtitle1" gutterBottom className={classes.title}>
          Unlock the power of your tokens
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom className={classes.description}>
          {formatDuration(maxStakeDuration)} of high-yield farm for {pool?.name}. Stake now to earn.
        </Typography>
        <FormControl className={classes.formControl}>
          <InputLabel shrink htmlFor='amount-input' className={classes.inputLabel}>Amount</InputLabel>
          <AmountInput id='amount-input' value={amount} onChange={(event) => setAmount(event.target.value)} type='number' error={isAmountValid} />
          {error && <FormHelperText error>{error}</FormHelperText>}
        </FormControl>
        <Typography variant="body2" color="textSecondary" className={classes.description}>
          Available: {tokenBalance} {tokenName}
        </Typography>
        <CardActions className={classes.buttons}>
          <Grid container className={classes.buttonsGrid}>
            <Grid item>
              <Button variant="contained" color="primary" className={classes.cancelButton} onClick={() => toggleView()} fullWidth>
                Cancel
              </Button>
            </Grid>
            <Grid item xs={7}>
              <Button variant="contained" color="primary" className={classes.approveButton} disabled={!isAmountValid || loading} fullWidth onClick={() => handleStakeAndApprove()}>
                {
                  loading
                    ? <CircularProgress
                        variant="indeterminate"
                        size={10}
                        thickness={2}
                        color="secondary" />
                    : approved
                      ? `Stake ${tokenName}`  
                      : `Approve ${tokenName}`
                }
              </Button>
            </Grid>
          </Grid>
        </CardActions>
      </CardContent>
    </Card>
  );
};

export default PartnerStakeApproveCard;
